import { colorsPalettes } from "@similarweb/styles";

import type { IRangeValues } from "pro-features/components/filtersPanel/src/RangeFilter";
import { translate } from "app/filters/i18nFilter";
import { EColumnsFields } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import { buildDefaultValuesFilterFromUrl } from "app/pages/digital-marketing/keywords-campaigns/reports/page-filters/KeywordsReportPageFiltersUtils";
import { SERP_FEATURES_MAP } from "app/common/serp/SerpFilterMaps";
import { getAppliedTagsText } from "app/pages/digital-marketing/keywords-campaigns/reports/page-filters/tags/utils";

type FilterLogicFunction = (filters: any) => string;
export enum EKWReportTableFilters {
    POSITIONS = "positions",
    CPC = "CPC",
    SERP_FILTER_TYPES = "serpFilterTypes",
    KWDIFF = "kwdiff",
    VOLUME = "volume",
    INTENT = "intent",
    TAGS = "tags",
    INCLUDE_KEYWORDS = "includeKeywords",
    EXCLUDE_KEYWORDS = "excludeKeywords",
}

export const UNTAGGED = "untagged";
export const TAGS_DELIMITER = ",";

export const KWReportWidgetTableHeadersMap: Record<EColumnsFields, string> = {
    [EColumnsFields.LAST_UPDATED]: "dashboard.metrics.rankTracker.KWReportTable.LAST_UPDATED.column",
    [EColumnsFields.KEYWORD]: "dashboard.metrics.rankTracker.KWReportTable.KEYWORD.column",
    [EColumnsFields.TAGS]: "dashboard.metrics.rankTracker.KWReportTable.TAGS.column",
    [EColumnsFields.GEO_LOCATION]: "dashboard.metrics.rankTracker.KWReportTable.GEO_LOCATION.column",
    [EColumnsFields.INTENT]: "dashboard.metrics.rankTracker.KWReportTable.INTENT.column",
    [EColumnsFields.CPC]: "dashboard.metrics.rankTracker.KWReportTable.CPC.column",
    [EColumnsFields.DIFFICULTY]: "dashboard.metrics.rankTracker.KWReportTable.DIFFICULTY.column",
    [EColumnsFields.SERP_FEATURES]: "dashboard.metrics.rankTracker.KWReportTable.SERP_FEATURES.column",
    [EColumnsFields.TARGET_URL]: "dashboard.metrics.rankTracker.KWReportTable.TOP_URL.column",
    [EColumnsFields.NUMBER_OF_URLS]: "rankTracker.keywordsReport.numUrls.title",
    [EColumnsFields.POSITION_TREND]: "dashboard.metrics.rankTracker.KWReportTable.POSITION_TREND.column",
    [EColumnsFields.VOLUME]: "dashboard.metrics.rankTracker.KWReportTable.VOLUME.column",
};

const getVolumeFilter = (volumeValues: IRangeValues): string => {
    let volume;
    if (!volumeValues.fromValue) {
        volume = `<= ${volumeValues.toValue}`;
    } else if (!volumeValues.toValue) {
        volume = `>= ${volumeValues.fromValue}`;
    } else {
        volume = `${Number(volumeValues.fromValue)}-${Number(volumeValues.toValue)}`;
    }
    return translate("dashboard.metrics.rankTracker.KWReportTable.volumeFilter", {
        volume,
    });
};

export const KWReportWidgetFiltersTooltipMap: Map<EKWReportTableFilters, FilterLogicFunction> = new Map([
    [
        EKWReportTableFilters.POSITIONS,
        (filters) =>
            filters.positions
                ? translate("dashboard.metrics.rankTracker.KWReportTable.positionsFilter", {
                      positions: filters.positions,
                  })
                : null,
    ],
    [
        EKWReportTableFilters.CPC,
        (filters) => {
            if (!filters.cpcFromValue && !filters.cpcToValue) {
                return null;
            }
            if (!filters.cpcFromValue) {
                return translate("dashboard.metrics.rankTracker.KWReportTable.cpcFilter", {
                    cpc: `=< ${filters.cpcToValue}`,
                });
            }
            if (!filters.cpcToValue) {
                return translate("dashboard.metrics.rankTracker.KWReportTable.cpcFilter", {
                    cpc: `>= ${filters.cpcFromValue}`,
                });
            }
            const cpc = `${Number(filters.cpcFromValue)}-${Number(filters.cpcToValue)}`;
            return translate("dashboard.metrics.rankTracker.KWReportTable.cpcFilter", { cpc });
        },
    ],
    [
        EKWReportTableFilters.SERP_FILTER_TYPES,
        (filters) => {
            if (!filters.serpFilterTypes) {
                return null;
            }
            const serpFeatures = filters.serpFilterTypes
                ?.split(",")
                .map((selectedType) => SERP_FEATURES_MAP[selectedType]?.name)
                .join(",");
            return translate("dashboard.metrics.rankTracker.KWReportTable.serpFeaturesFilter", {
                serpFeatures,
            });
        },
    ],
    [
        EKWReportTableFilters.KWDIFF,
        (filters) => {
            const KDValues: IRangeValues = buildDefaultValuesFilterFromUrl(filters.rangeFilter, "kwdiff");
            if (!KDValues.fromValue && !KDValues.toValue) {
                return null;
            }
            const kd = `${Number(KDValues.fromValue)}-${Number(KDValues.toValue)}`;
            return translate("dashboard.metrics.rankTracker.KWReportTable.kdFilter", {
                kd,
            });
        },
    ],
    [
        EKWReportTableFilters.VOLUME,
        (filters) => {
            const volumeValues: IRangeValues = buildDefaultValuesFilterFromUrl(filters.rangeFilter, "volume");
            if (!volumeValues.fromValue && !volumeValues.toValue) {
                return null;
            }
            return getVolumeFilter(volumeValues);
        },
    ],
    [
        EKWReportTableFilters.INTENT,
        (filters) =>
            filters.intentsFilter
                ? translate("dashboard.metrics.rankTracker.KWReportTable.intentsFilter", {
                      intent: filters.intentsFilter,
                  })
                : null,
    ],
    [
        EKWReportTableFilters.TAGS,
        (filters) => {
            const includeTags = filters.tags?.split(TAGS_DELIMITER).filter(Boolean) ?? [];
            const excludeTags = filters.excludeTags?.split(TAGS_DELIMITER).filter(Boolean) ?? [];

            // Handle untaggedFilter (new) and withUntagged (legacy)
            const hasUntagged =
                filters.untaggedFilter === "include" ||
                filters.untaggedFilter === "exclude" ||
                filters.withUntagged === "true";

            const totalCount = includeTags.length + excludeTags.length + (hasUntagged ? 1 : 0);

            if (totalCount === 0) {
                return null;
            }

            return getAppliedTagsText(
                {
                    includeTags,
                    excludeTags,
                    untaggedFilter: filters.untaggedFilter ?? (filters.withUntagged === "true" ? "include" : undefined),
                },
                translate,
            );
        },
    ],
    [
        EKWReportTableFilters.INCLUDE_KEYWORDS,
        (filters) =>
            filters.includeKeywords
                ? translate("dashboard.metrics.rankTracker.KWReportTable.includeKeywordsFilter", {
                      includeKeywords: filters.includeKeywords,
                  })
                : null,
    ],
    [
        EKWReportTableFilters.EXCLUDE_KEYWORDS,
        (filters) =>
            filters.excludeKeywords
                ? translate("dashboard.metrics.rankTracker.KWReportTable.excludeKeywordsFilter", {
                      excludeKeywords: filters.excludeKeywords,
                  })
                : null,
    ],
]);

export const KWReportTableWidgetAPIParams = [
    "campaignId",
    "duration",
    "locationsIds",
    "graphTab",
    "tableTab",
    "device",
    "graphGranularity",
    "tableGranularity",
    "sites",
    "sortedColumn",
    "asc",
    "rangeFilter",
    "intentsFilter",
    "excludeUrls",
    "includeUrls",
    "excludeKeywords",
    "includeKeywords",
    "cpcFromValue",
    "cpcToValue",
    "serpFilterGroup",
    "serpFilterTypes",
    "positions",
    "withUntagged",
    "untaggedFilter",
    "tags",
    "excludeTags",
    "MainSitePositionTrend",
    "useCaseFilter",
    "tableView",
];

export const COLOR_OF_SECONDARY_SITES = colorsPalettes.carbon[200];
export const SECONDARY_SITES_KEY = "Others";
